import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';

// Workshop Step Types
export type WorkshopStep = 'landing' | 'challenge' | 'practice' | 'transformation' | 'assessment' | 'cta';

// Skill Mastery Interface
export interface SkillMastery {
  skill: string;
  description: string;
  mastered: boolean;
}

// Conversation Interface for Lyra coaching
export interface ConversationExchange {
  user: string;
  lyra: string;
  timestamp: Date;
}

// Workshop State Interface
export interface WorkshopState {
  currentStep: WorkshopStep;
  workshopProgress: number;
  skills: SkillMastery[];
  conversationHistory: ConversationExchange[];
  userInput: string;
  lyraResponse: string;
  isLyraThinking: boolean;
  improvementMade: boolean;
  completedSteps: WorkshopStep[];
  startTime: Date | null;
  completionTime: Date | null;
}

// Action Types
type WorkshopAction =
  | { type: 'SET_CURRENT_STEP'; payload: WorkshopStep }
  | { type: 'SET_USER_INPUT'; payload: string }
  | { type: 'SET_LYRA_RESPONSE'; payload: string }
  | { type: 'SET_LYRA_THINKING'; payload: boolean }
  | { type: 'ADD_CONVERSATION'; payload: { user: string; lyra: string } }
  | { type: 'MASTER_SKILL'; payload: number }
  | { type: 'SET_IMPROVEMENT_MADE'; payload: boolean }
  | { type: 'COMPLETE_STEP'; payload: WorkshopStep }
  | { type: 'RESET_WORKSHOP' }
  | { type: 'LOAD_SAVED_STATE'; payload: Partial<WorkshopState> }
  | { type: 'START_WORKSHOP' }
  | { type: 'COMPLETE_WORKSHOP' };

// Initial State
const initialState: WorkshopState = {
  currentStep: 'landing',
  workshopProgress: 0,
  skills: [
    { skill: 'Context Setting', description: 'Providing specific role and situation details', mastered: false },
    { skill: 'Impact Specification', description: 'Including concrete outcomes and metrics', mastered: false },
    { skill: 'Voice Alignment', description: 'Matching tone to audience and mission', mastered: false },
    { skill: 'Quality Control', description: 'Spotting and fixing AI output issues', mastered: false },
    { skill: 'Professional Polish', description: 'Creating mission-aligned, authentic content', mastered: false }
  ],
  conversationHistory: [],
  userInput: '',
  lyraResponse: '',
  isLyraThinking: false,
  improvementMade: false,
  completedSteps: [],
  startTime: null,
  completionTime: null
};

// Reducer Function
function workshopReducer(state: WorkshopState, action: WorkshopAction): WorkshopState {
  switch (action.type) {
    case 'SET_CURRENT_STEP':
      return {
        ...state,
        currentStep: action.payload,
        workshopProgress: calculateProgress(action.payload)
      };

    case 'SET_USER_INPUT':
      return {
        ...state,
        userInput: action.payload
      };

    case 'SET_LYRA_RESPONSE':
      return {
        ...state,
        lyraResponse: action.payload
      };

    case 'SET_LYRA_THINKING':
      return {
        ...state,
        isLyraThinking: action.payload
      };

    case 'ADD_CONVERSATION':
      return {
        ...state,
        conversationHistory: [
          ...state.conversationHistory,
          {
            user: action.payload.user,
            lyra: action.payload.lyra,
            timestamp: new Date()
          }
        ]
      };

    case 'MASTER_SKILL':
      return {
        ...state,
        skills: state.skills.map((skill, index) =>
          index === action.payload ? { ...skill, mastered: true } : skill
        )
      };

    case 'SET_IMPROVEMENT_MADE':
      return {
        ...state,
        improvementMade: action.payload
      };

    case 'COMPLETE_STEP':
      const newCompletedSteps = state.completedSteps.includes(action.payload)
        ? state.completedSteps
        : [...state.completedSteps, action.payload];

      return {
        ...state,
        completedSteps: newCompletedSteps
      };

    case 'START_WORKSHOP':
      return {
        ...state,
        startTime: new Date(),
        currentStep: 'challenge'
      };

    case 'COMPLETE_WORKSHOP':
      return {
        ...state,
        completionTime: new Date(),
        currentStep: 'cta'
      };

    case 'RESET_WORKSHOP':
      return {
        ...initialState,
        startTime: new Date()
      };

    case 'LOAD_SAVED_STATE':
      return {
        ...state,
        ...action.payload,
        // Ensure dates are properly converted from localStorage
        startTime: action.payload.startTime ? new Date(action.payload.startTime) : state.startTime,
        completionTime: action.payload.completionTime ? new Date(action.payload.completionTime) : state.completionTime,
        conversationHistory: action.payload.conversationHistory?.map(conv => ({
          ...conv,
          timestamp: new Date(conv.timestamp)
        })) || state.conversationHistory
      };

    default:
      return state;
  }
}

// Helper function to calculate progress
function calculateProgress(currentStep: WorkshopStep): number {
  const steps = ['landing', 'challenge', 'practice', 'transformation', 'assessment', 'cta'];
  const currentIndex = steps.indexOf(currentStep);
  return currentIndex >= 0 ? (currentIndex / (steps.length - 1)) * 100 : 0;
}

// Context
interface WorkshopContextType {
  state: WorkshopState;
  dispatch: React.Dispatch<WorkshopAction>;
  // Helper functions
  navigateToStep: (step: WorkshopStep) => void;
  masterSkill: (skillIndex: number) => void;
  addConversation: (user: string, lyra: string) => void;
  resetWorkshop: () => void;
  saveProgress: () => void;
  loadProgress: () => void;
  getNextStep: () => WorkshopStep | null;
  getPreviousStep: () => WorkshopStep | null;
  canNavigateNext: () => boolean;
  canNavigatePrevious: () => boolean;
  getStepProgress: () => { current: number; total: number };
  getMasteredSkillsCount: () => number;
  getWorkshopDuration: () => string | null;
}

const WorkshopContext = createContext<WorkshopContextType | undefined>(undefined);

// Constants
const STORAGE_KEY = 'lyra-workshop-progress';
const STEPS_ORDER: WorkshopStep[] = ['landing', 'challenge', 'practice', 'transformation', 'assessment', 'cta'];

// Provider Component
interface WorkshopProgressProviderProps {
  children: ReactNode;
}

export const WorkshopProgressProvider: React.FC<WorkshopProgressProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(workshopReducer, initialState);

  // Load saved progress on mount
  useEffect(() => {
    loadProgress();
  }, []);

  // Auto-save progress when state changes
  useEffect(() => {
    if (state.startTime) {
      saveProgress();
    }
  }, [state]);

  // Helper Functions
  const navigateToStep = (step: WorkshopStep) => {
    dispatch({ type: 'SET_CURRENT_STEP', payload: step });
    dispatch({ type: 'COMPLETE_STEP', payload: state.currentStep });
  };

  const masterSkill = (skillIndex: number) => {
    dispatch({ type: 'MASTER_SKILL', payload: skillIndex });
  };

  const addConversation = (user: string, lyra: string) => {
    dispatch({ type: 'ADD_CONVERSATION', payload: { user, lyra } });
  };

  const resetWorkshop = () => {
    localStorage.removeItem(STORAGE_KEY);
    dispatch({ type: 'RESET_WORKSHOP' });
  };

  const saveProgress = () => {
    try {
      const progressData = {
        ...state,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(progressData));
    } catch (error) {
      console.error('Failed to save workshop progress:', error);
    }
  };

  const loadProgress = () => {
    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        dispatch({ type: 'LOAD_SAVED_STATE', payload: parsedData });
      }
    } catch (error) {
      console.error('Failed to load workshop progress:', error);
    }
  };

  const getNextStep = (): WorkshopStep | null => {
    const currentIndex = STEPS_ORDER.indexOf(state.currentStep);
    if (currentIndex < STEPS_ORDER.length - 1) {
      return STEPS_ORDER[currentIndex + 1];
    }
    return null;
  };

  const getPreviousStep = (): WorkshopStep | null => {
    const currentIndex = STEPS_ORDER.indexOf(state.currentStep);
    if (currentIndex > 0) {
      return STEPS_ORDER[currentIndex - 1];
    }
    return null;
  };

  const canNavigateNext = (): boolean => {
    // Define conditions for when user can proceed to next step
    switch (state.currentStep) {
      case 'landing':
        return true;
      case 'challenge':
        return true;
      case 'practice':
        return state.userInput.length >= 50; // Minimum input requirement
      case 'transformation':
        return state.improvementMade;
      case 'assessment':
        return state.skills.filter(skill => skill.mastered).length >= 3;
      case 'cta':
        return false; // Final step
      default:
        return false;
    }
  };

  const canNavigatePrevious = (): boolean => {
    return state.currentStep !== 'landing';
  };

  const getStepProgress = () => {
    const currentIndex = STEPS_ORDER.indexOf(state.currentStep);
    return {
      current: currentIndex + 1,
      total: STEPS_ORDER.length
    };
  };

  const getMasteredSkillsCount = (): number => {
    return state.skills.filter(skill => skill.mastered).length;
  };

  const getWorkshopDuration = (): string | null => {
    if (!state.startTime) return null;

    const endTime = state.completionTime || new Date();
    const durationMs = endTime.getTime() - state.startTime.getTime();
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  const contextValue: WorkshopContextType = {
    state,
    dispatch,
    navigateToStep,
    masterSkill,
    addConversation,
    resetWorkshop,
    saveProgress,
    loadProgress,
    getNextStep,
    getPreviousStep,
    canNavigateNext,
    canNavigatePrevious,
    getStepProgress,
    getMasteredSkillsCount,
    getWorkshopDuration
  };

  return (
    <WorkshopContext.Provider value={contextValue}>
      {children}
    </WorkshopContext.Provider>
  );
};

// Hook
export const useWorkshopProgress = () => {
  const context = useContext(WorkshopContext);
  if (context === undefined) {
    throw new Error('useWorkshopProgress must be used within a WorkshopProgressProvider');
  }
  return context;
};

export default WorkshopProgressProvider;